/**
 * What the SDK does when POST /sites/validate-client-key answers 429.
 *
 * A 429 is "no answer yet", not "no": floe-api refused the request before it
 * looked at the key. Reading it as a rejected key left the page with no
 * widget for the whole page view, although the limit it hit resets within a
 * minute. So the SDK waits for the time floe-api names in `Retry-After` and
 * asks once more. Once: a page that is still throttled then gets no widget,
 * on every surface. It does not fail open the way an unreachable floe-api does
 * for onboarding, because anyone can cause a 429: failing open on one would
 * let a third party mount the agent on a site that has turned it off, before
 * the site's consent default has been read.
 */

/** floe-api's window is a minute, so nothing it names is longer than that. */
const MAX_RETRY_DELAY_MS = 60_000;
const MIN_RETRY_DELAY_MS = 1000;
/**
 * `Retry-After` is readable cross-origin only when floe-api exposes it, which
 * a deployment older than this SDK does not.
 */
const DEFAULT_RETRY_DELAY_MS = 10_000;
/**
 * Pages throttled in the same window are all told the same instant. Spread
 * them, so the retries do not arrive together and fill the next window.
 */
const RETRY_JITTER_MS = 3000;

const WHOLE_SECONDS = /^\d+$/u;

/** Milliseconds to wait before the one retry, given the 429's `Retry-After`. */
export const throttledRetryDelayMs = (
  retryAfter: string | null,
  random: () => number = Math.random,
): number => {
  const seconds = retryAfter?.trim() ?? '';
  const requestedMs = WHOLE_SECONDS.test(seconds)
    ? Number(seconds) * 1000
    : DEFAULT_RETRY_DELAY_MS;
  const bounded = Math.min(
    Math.max(requestedMs, MIN_RETRY_DELAY_MS),
    MAX_RETRY_DELAY_MS,
  );
  return bounded + Math.floor(random() * RETRY_JITTER_MS);
};

export interface CancellableWait {
  /** Ends the wait now; `elapsed` then resolves false. */
  cancel: () => void;
  /** True once the time has passed, false if the wait was cancelled first. */
  elapsed: Promise<boolean>;
}

/**
 * A wait the SDK can abandon. The retry wait can be most of a minute, and a
 * host that tears the SDK down in that time must not get a widget afterwards.
 */
export const cancellableWait = (ms: number): CancellableWait => {
  const cancelled = new AbortController();
  // oxlint-disable-next-line promise/avoid-new -- setTimeout and an abort signal are callback APIs, and Promise.withResolvers is too recent for the browsers the SDK runs in.
  const elapsed = new Promise<boolean>((resolve) => {
    const timer = setTimeout(() => {
      resolve(true);
    }, ms);
    cancelled.signal.addEventListener(
      'abort',
      () => {
        clearTimeout(timer);
        resolve(false);
      },
      { once: true },
    );
  });
  return {
    cancel: () => {
      cancelled.abort();
    },
    elapsed,
  };
};
